import { Accordion } from '@/components';
import { PortableText } from '@portabletext/react';
import { portableTextComponents } from '@/utils/portableTextComponents';
import { VEHICLE_BY_SLUG_QUERYResult } from 'sanity.types';

const SectionHeader = ({ title }: { title: string }) => {
    return (
        <div className="flex rounded-md bg-gray-100 w-full text-xl font-bold p-2 h-12 items-center">
            {title}
        </div>
    );
};

const VehicleFeaturesBody = ({ features }: { features: string[] }) => {
    return (
        <div className="flex flex-col w-full md:w-1/2 p-4">
            <SectionHeader title="Features" />
            <div className="feature-list z-[1] p-4">
                <ul className="max-w-[90%] sm:max-w-[80%] lg:max-w-[90%]">
                    {features.map((feature: string, idx: number) => (
                        <li key={idx}>
                            <span>{feature}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

type VehicleData = NonNullable<VEHICLE_BY_SLUG_QUERYResult>;

interface VehicleDescriptionProps {
    vehicleData: VehicleData;
}

const VehicleDescription = ({ vehicleData }: VehicleDescriptionProps) => {
    if (!vehicleData) return null;
    const { insuranceExcess, mileageExcess, content } = vehicleData;
    const features = (vehicleData?.features ?? []).filter(
        (feature): feature is string => typeof feature === 'string'
    );
    const { legalContent } = content[0] || {};

    const components = portableTextComponents;

    const ukDrivingLicenseDocuments = (
        <PortableText
            value={legalContent?.ukDrivingLicenseDocuments ?? []}
            components={components}
        />
    );

    const nonUkDrivingLicenseDocuments = (
        <PortableText
            value={legalContent?.nonUkDrivingLicenseDocuments ?? []}
            components={components}
        />
    );

    return (
        <div className="mx-auto w-full md:w-[700px] xl:w-[800px] mb-4 p-4">
            <h2 className="text-3xl font-bold mx-auto mb-4 text-center">
                Features and specifications
            </h2>
            <div className="flex flex-col md:flex-row">
                <VehicleFeaturesBody features={features} />
                <div className="flex w-full flex-col md:w-1/2 p-4">
                    <SectionHeader title="Insurance & Excess Mileage" />
                    <div className="feature-list p-4">
                        <ul className="max-w-[90%] sm:max-w-[80%] lg:max-w-[90%]">
                            <li>
                                <span>{insuranceExcess}</span>
                            </li>
                            <li>
                                <span>{mileageExcess}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div className="flex w-full flex-col p-4">
                <SectionHeader title="Age restrictions" />
                <div className="feature-list p-4">
                    <ul className="max-w-[90%] sm:max-w-[80%] lg:max-w-[90%]">
                        <li>
                            <span>{legalContent?.ageRestrictions}</span>
                        </li>
                    </ul>
                </div>
            </div>
            <div className="flex w-full flex-col p-4 text-center">
                <h3 className="text-3xl font-bold mx-auto">
                    Documents you will need to bring
                </h3>

                <Accordion
                    title="UK Driving license holders:"
                    content={ukDrivingLicenseDocuments}
                />

                <Accordion
                    title="NON-UK Driving license holders:"
                    content={nonUkDrivingLicenseDocuments}
                />
            </div>
        </div>
    );
};

export default VehicleDescription;
